<template>
  <section class="acquisition-profitability-report">
    <div class="profitability-heading">
      <div>
        <span class="section-eyebrow">Adquisición y monetización</span>
        <h3>Rentabilidad de adquisición</h3>
        <p>
          Valor neto de puntos comprados y de puntos incluidos en planes,
          comparado con el gasto reportado por Google Ads y Meta Ads.
        </p>
      </div>
      <div class="heading-actions">
        <span v-if="periodLabel" class="report-pill">{{ periodLabel }}</span>
        <span v-if="report && report.period.is_partial_day" class="report-pill partial-pill">
          Día parcial
        </span>
        <button
          type="button"
          class="refresh-button"
          :disabled="isLoading"
          @click="fetchProfitability(true)"
        >
          <b-icon icon="arrow-clockwise" />
          {{ isLoading ? 'Actualizando…' : 'Actualizar' }}
        </button>
      </div>
    </div>

    <div v-if="error" class="profitability-alert error-alert" role="alert">
      <b-icon icon="exclamation-triangle-fill" />
      <span>{{ error }}</span>
    </div>

    <div v-if="isLoading && !report" class="profitability-loading">
      <div class="loading-spinner" aria-hidden="true"></div>
      <strong>Calculando rentabilidad…</strong>
      <span>Consultando Jobbing, Google Ads y Meta Ads.</span>
    </div>

    <template v-if="report">
      <div v-if="isLoading" class="profitability-alert loading-alert">
        <b-icon icon="arrow-repeat" animation="spin" />
        <span>Actualizando el corte; las cifras visibles corresponden al resultado anterior.</span>
      </div>

      <div class="report-meta-row">
        <span>
          Corte: <strong>{{ formatCutoff(report.period.as_of) }}</strong>
        </span>
        <span>
          Base: <strong>CLP netos sin IVA</strong>
        </span>
        <span>
          IVA descargado: <strong>{{ formatPercent(report.basis.vat_rate) }}</strong>
        </span>
        <span v-if="report.cache && report.cache.hit">
          Caché: <strong>{{ formatCacheAge(report.cache.age_seconds) }}</strong>
        </span>
      </div>

      <div class="financial-kpi-grid">
        <article class="financial-kpi-card income-card">
          <span class="kpi-label">Valor atribuido sin IVA, incluidos planes</span>
          <strong>{{ formatClp(summary.income_net_clp) }}</strong>
          <small>Paquetes consumidos: {{ formatClp(summary.purchased_points_income_net_clp) }} · Planes: {{ formatClp(summary.plan_transfer_net_clp) }}</small>
        </article>
        <article class="financial-kpi-card spend-card">
          <span class="kpi-label">Gasto Ads observado</span>
          <strong>{{ formatClp(summary.observed_spend_clp) }}</strong>
          <small>
            Google {{ formatClp(summary.google_spend_clp) }} ·
            Meta {{ formatClp(summary.meta_spend_clp) }}
          </small>
        </article>
        <article
          class="financial-kpi-card"
          :class="profitValueClass(summary.estimated_profit_clp)"
        >
          <span class="kpi-label">Resultado de adquisición, incluidos planes</span>
          <strong v-if="report.basis.financial_result_available">
            {{ formatClp(summary.estimated_profit_clp) }}
          </strong>
          <strong v-else>—</strong>
          <small>Solo puntos comprados: {{ purchasedOnlyResult === null ? '—' : formatClp(purchasedOnlyResult) }} · Antes de otros costos</small>
        </article>
        <article
          class="financial-kpi-card"
          :class="profitValueClass(summary.estimated_margin)"
        >
          <span class="kpi-label">Margen estimado</span>
          <strong v-if="report.basis.financial_result_available">
            {{ formatPercent(summary.estimated_margin) }}
          </strong>
          <strong v-else>—</strong>
          <small>No equivale a margen contable</small>
        </article>
      </div>

      <div class="profitability-alert">
        <span>
          Se asignan {{ formatClp(summary.plan_transfer_net_clp) }} de suscripciones a adquisición.
          Ese mismo importe se descuenta del resultado tras puntos en Suscripciones; los ingresos completos de las cuotas siguen visibles allí.
          Los {{ formatNumber(report.coverage.net_plan_points) }} puntos netos incluidos en planes,
          usados en {{ formatNumber(report.coverage.requests_using_plan_points) }} solicitudes de la cohorte,
          cuentan como accesos y reciben una imputación interna, sin otra venta ni consumir saldo comprado en el FIFO.
          Se usa el valor neto promedio de paquetes pagados hasta cada cuota, limitado al neto de esa cuota por punto acreditado.
          Control de paquetes consumidos con IVA: {{ formatClp(summary.income_including_vat_clp) }}.
        </span>
      </div>

      <div class="volume-kpi-grid">
        <div class="volume-kpi">
          <span>Solicitudes ofertadas</span>
          <strong>{{ formatNumber(summary.requests) }}</strong>
        </div>
        <div class="volume-kpi">
          <span>Solicitudes vendidas</span>
          <strong>{{ formatNumber(summary.sold_requests) }}</strong>
        </div>
        <div class="volume-kpi">
          <span>Compras netas</span>
          <strong>{{ formatNumber(summary.net_purchases) }}</strong>
        </div>
        <div class="volume-kpi">
          <span>ROAS atribuido, incluidos planes</span>
          <strong>{{ formatRoas(summary.paid_roas) }}</strong>
        </div>
      </div>

      <div class="report-section daily-section">
        <div class="section-heading-row">
          <div>
            <h4>Evolución diaria</h4>
            <p>Valor atribuido con planes, gasto total observado y resultado estimado por fecha civil.</p>
          </div>
          <span class="section-count">{{ report.daily.length }} días</span>
        </div>
        <div v-if="hasDailyData" class="profitability-chart-container">
          <line-chart
            :key="'acquisition-daily-' + chartKey"
            :chart-data="dailyChartData"
            :options="dailyChartOptions"
            :height="340"
          />
        </div>
        <p v-else class="empty-state">No hay resultados diarios para este período.</p>
      </div>

      <div class="report-section">
        <div class="section-heading-row">
          <div>
            <h4>Comparación por plataforma</h4>
            <p>La contribución publicitaria usa únicamente el gasto dentro del alcance de adquisición.</p>
          </div>
        </div>
        <div v-if="report.platforms.length" class="profitability-table-wrap">
          <table class="profitability-table platform-table">
            <thead>
              <tr>
                <th>Plataforma</th>
                <th>Solicitudes</th>
                <th>Vendidas</th>
                <th>Valor sin IVA con planes</th>
                <th>Gasto</th>
                <th>Contribución</th>
                <th>ROAS</th>
                <th>Cobertura campaña</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="platform in report.platforms" :key="platform.platform">
                <td>
                  <span class="platform-dot" :class="platformClass(platform.platform)"></span>
                  <strong>{{ platform.platform }}</strong>
                </td>
                <td>{{ formatNumber(platform.requests) }}</td>
                <td>{{ formatNumber(platform.sold_requests) }}</td>
                <td>{{ formatClp(platform.sales_net_clp) }}</td>
                <td>{{ formatClp(platform.ads_cost_clp) }}</td>
                <td :class="numberValueClass(platform.contribution_clp)">
                  {{ formatClp(platform.contribution_clp) }}
                </td>
                <td :class="roasValueClass(platform.roas)">{{ formatRoas(platform.roas) }}</td>
                <td>{{ formatPercent(platform.campaign_match_rate) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p v-else class="empty-state">No hay plataformas con costos reconciliados.</p>
        <div v-if="summary.meta_out_of_scope_spend_clp > 0" class="scope-note">
          <b-icon icon="info-circle-fill" />
          <span>
            Meta registra {{ formatClp(summary.meta_out_of_scope_spend_clp) }} fuera del ROAS
            de adquisición. Este gasto sí forma parte del resultado financiero global.
          </span>
        </div>
      </div>

      <div class="report-section">
        <div class="section-heading-row">
          <div>
            <h4>Rentabilidad por campaña</h4>
            <p>Incluye el costo completo del período, también en días sin solicitudes reconciliadas.</p>
          </div>
          <span class="section-count">{{ report.campaigns.length }} campañas</span>
        </div>
        <div v-if="report.campaigns.length" class="profitability-table-wrap">
          <table class="profitability-table campaign-table">
            <thead>
              <tr>
                <th>Campaña</th>
                <th>Solicitudes</th>
                <th>Vendidas</th>
                <th>Valor sin IVA con planes</th>
                <th>Gasto</th>
                <th>Contribución</th>
                <th>ROAS</th>
                <th>Evolución</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="campaign in report.campaigns"
                :key="campaign.platform + '-' + campaign.campaign_id"
              >
                <td class="campaign-cell">
                  <span class="platform-label">{{ campaign.platform }}</span>
                  <strong>{{ campaign.campaign_name }}</strong>
                  <small v-if="campaign.configured_utm_campaign">
                    UTM: {{ campaign.configured_utm_campaign }}
                  </small>
                </td>
                <td>{{ formatNumber(campaign.requests) }}</td>
                <td>{{ formatNumber(campaign.sold_requests) }}</td>
                <td>{{ formatClp(campaign.sales_net_clp) }}</td>
                <td>{{ formatClp(campaign.ads_cost_clp) }}</td>
                <td :class="numberValueClass(campaign.contribution_clp)">
                  {{ formatClp(campaign.contribution_clp) }}
                </td>
                <td :class="roasValueClass(campaign.roas)">{{ formatRoas(campaign.roas) }}</td>
                <td>
                  <span
                    class="signal-badge"
                    :class="signalClass(campaign.learning_signal)"
                    :title="campaign.learning_reason || ''"
                  >
                    {{ signalLabel(campaign.learning_signal) }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p v-else class="empty-state">No hay campañas para este período.</p>
      </div>

      <div class="two-column-sections">
        <div class="report-section compact-section">
          <div class="section-heading-row">
            <div>
              <h4>Categorías pagadas</h4>
              <p>Asignación estimada mediante reparto de costo por canal y día.</p>
            </div>
          </div>
          <div v-if="topCategories.length" class="profitability-table-wrap">
            <table class="profitability-table compact-table">
              <thead>
                <tr>
                  <th>Categoría</th>
                  <th>Gasto</th>
                  <th>Contribución</th>
                  <th>ROAS</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="category in topCategories"
                  :key="category.platform + '-' + category.category_name"
                >
                  <td class="campaign-cell">
                    <span class="platform-label">{{ category.platform }}</span>
                    <strong>{{ category.category_name }}</strong>
                  </td>
                  <td>{{ formatClp(category.ads_cost_clp) }}</td>
                  <td :class="numberValueClass(category.contribution_clp)">
                    {{ formatClp(category.contribution_clp) }}
                  </td>
                  <td :class="roasValueClass(category.roas)">{{ formatRoas(category.roas) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p v-else class="empty-state">No hay categorías pagadas reconciliadas.</p>
        </div>

        <div class="report-section compact-section">
          <div class="section-heading-row">
            <div>
              <h4>Fuentes no pagadas</h4>
              <p>Contribución antes de costos SEO, contenido, herramientas u operación.</p>
            </div>
          </div>
          <div v-if="report.sources.length" class="profitability-table-wrap">
            <table class="profitability-table compact-table">
              <thead>
                <tr>
                  <th>Fuente</th>
                  <th>Solicitudes</th>
                  <th>Vendidas</th>
                  <th>Valor neto</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="source in report.sources"
                  :key="source.source_type + '-' + source.source_name"
                >
                  <td class="campaign-cell">
                    <span class="platform-label neutral-label">{{ source.source_type }}</span>
                    <strong>{{ source.source_name }}</strong>
                  </td>
                  <td>{{ formatNumber(source.requests) }}</td>
                  <td>{{ formatNumber(source.sold_requests) }}</td>
                  <td>{{ formatClp(source.sales_net_clp) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p v-else class="empty-state">No se identificaron fuentes orgánicas o propias.</p>
        </div>
      </div>

      <div class="report-section audit-section">
        <div class="section-heading-row">
          <div>
            <h4>Cobertura y cautelas</h4>
            <p>Controles necesarios para interpretar las cifras sin sobreafirmar.</p>
          </div>
          <span
            class="balance-badge"
            :class="{ balanced: report.coverage.cohort_reconciliation_balanced }"
          >
            {{ report.coverage.cohort_reconciliation_balanced ? 'Cohorte conciliada' : 'Revisar conciliación' }}
          </span>
        </div>
        <div class="coverage-grid">
          <div>
            <span>Cobertura campaña Google</span>
            <strong>{{ formatPercent(report.coverage.google_campaign_match_rate) }}</strong>
          </div>
          <div>
            <span>Cobertura campaña Meta</span>
            <strong>{{ formatPercent(report.coverage.meta_campaign_match_rate) }}</strong>
          </div>
          <div>
            <span>Puntos sin precio FIFO</span>
            <strong>{{ formatNumber(report.coverage.cohort_unpriced_points) }}</strong>
          </div>
          <div>
            <span>Solicitudes estado 10 incluidas</span>
            <strong>{{ formatNumber(report.coverage.included_state_10_requests) }}</strong>
          </div>
        </div>
        <ul class="warning-list">
          <li v-for="warning in report.warnings" :key="warning">{{ warning }}</li>
        </ul>
      </div>
    </template>
  </section>
</template>

<script>
import axios from 'axios';
import LineChart from '@/components/charts/LineChart.js';

export default {
  name: 'AcquisitionProfitabilityReport',
  components: {
    LineChart,
  },
  props: {
    startDate: {
      type: String,
      required: true,
    },
    endDate: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      report: null,
      isLoading: false,
      error: null,
      chartKey: 0,
      requestSerial: 0,
      reloadTimer: null,
    };
  },
  computed: {
    summary() {
      return this.report && this.report.summary ? this.report.summary : {};
    },
    purchasedOnlyResult() {
      if (!this.report || !this.report.basis.financial_result_available) return null;
      return this.summary.purchased_points_income_net_clp - this.summary.observed_spend_clp;
    },
    periodLabel() {
      if (!this.startDate || !this.endDate) return '';
      return `${this.formatDate(this.startDate)} – ${this.formatDate(this.endDate)}`;
    },
    hasDailyData() {
      return Boolean(this.report && Array.isArray(this.report.daily) && this.report.daily.length);
    },
    topCategories() {
      if (!this.report || !Array.isArray(this.report.categories)) return [];
      return this.report.categories.slice(0, 12);
    },
    dailyChartData() {
      const rows = this.report && Array.isArray(this.report.daily) ? this.report.daily : [];
      const datasets = [
        {
          label: 'Valor sin IVA, incluidos planes',
          data: rows.map(row => row.sales_net_clp),
          borderColor: '#188977',
          backgroundColor: 'rgba(24, 137, 119, 0.10)',
          pointBackgroundColor: '#188977',
          fill: false,
          borderWidth: 2,
          lineTension: 0.2,
        },
        {
          label: 'Gasto Ads observado',
          data: rows.map(row => row.observed_spend_clp),
          borderColor: '#e28743',
          backgroundColor: 'rgba(226, 135, 67, 0.08)',
          pointBackgroundColor: '#e28743',
          fill: false,
          borderWidth: 2,
          lineTension: 0.2,
        },
      ];
      if (this.report && this.report.basis && this.report.basis.financial_result_available) {
        datasets.push({
          label: 'Utilidad estimada',
          data: rows.map(row => row.estimated_profit_clp),
          borderColor: '#315f8c',
          backgroundColor: 'rgba(49, 95, 140, 0.08)',
          pointBackgroundColor: rows.map(row => row.estimated_profit_clp >= 0 ? '#188977' : '#c84f4f'),
          fill: false,
          borderDash: [5, 4],
          borderWidth: 2,
          lineTension: 0.2,
        });
      }
      return {
        labels: rows.map(row => this.shortDate(row.date, row.is_partial_day)),
        datasets,
      };
    },
    dailyChartOptions() {
      const numberFormatter = value => `$${new Intl.NumberFormat('es-CL', {
        maximumFractionDigits: 0,
      }).format(value || 0)}`;
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: true,
          position: 'bottom',
          labels: { boxWidth: 12, padding: 18 },
        },
        tooltips: {
          mode: 'index',
          intersect: false,
          callbacks: {
            label(tooltipItem, data) {
              const dataset = data.datasets[tooltipItem.datasetIndex];
              return `${dataset.label}: ${numberFormatter(tooltipItem.yLabel)}`;
            },
          },
        },
        scales: {
          xAxes: [{
            gridLines: { display: false },
            ticks: { maxRotation: 45, minRotation: 0, autoSkip: true },
          }],
          yAxes: [{
            gridLines: { color: 'rgba(99, 115, 129, 0.10)' },
            ticks: { callback: numberFormatter },
            scaleLabel: { display: true, labelString: 'CLP' },
          }],
        },
      };
    },
  },
  watch: {
    startDate() {
      this.scheduleFetch();
    },
    endDate() {
      this.scheduleFetch();
    },
  },
  mounted() {
    this.fetchProfitability(false);
  },
  beforeDestroy() {
    if (this.reloadTimer) {
      window.clearTimeout(this.reloadTimer);
    }
  },
  methods: {
    scheduleFetch() {
      if (this.reloadTimer) {
        window.clearTimeout(this.reloadTimer);
      }
      this.reloadTimer = window.setTimeout(() => {
        this.reloadTimer = null;
        this.fetchProfitability(false);
      }, 120);
    },
    async fetchProfitability(forceRefresh = false) {
      if (!this.startDate || !this.endDate || this.startDate > this.endDate) {
        this.error = 'Selecciona un rango de fechas válido.';
        return;
      }
      const serial = ++this.requestSerial;
      this.isLoading = true;
      this.error = null;
      try {
        const hostname = window.hostname || '';
        const response = await axios.get(`${hostname}api/admin/acquisition_profitability`, {
          params: {
            start_date: this.startDate,
            end_date: this.endDate,
            refresh: forceRefresh ? 1 : 0,
          },
        });
        if (serial !== this.requestSerial) return;
        this.report = response && response.data ? response.data : null;
        this.chartKey += 1;
      } catch (error) {
        if (serial !== this.requestSerial) return;
        this.error = (
          error.response?.data?.error
          || error.response?.data?.message
          || 'No se pudo cargar la rentabilidad de adquisición.'
        );
      } finally {
        if (serial === this.requestSerial) {
          this.isLoading = false;
        }
      }
    },
    formatNumber(value) {
      const numeric = Number(value);
      return new Intl.NumberFormat('es-CL', { maximumFractionDigits: 0 }).format(
        Number.isFinite(numeric) ? numeric : 0
      );
    },
    formatClp(value) {
      const numeric = Number(value);
      if (!Number.isFinite(numeric)) return '—';
      const sign = numeric < 0 ? '-' : '';
      return `${sign}$${new Intl.NumberFormat('es-CL', {
        maximumFractionDigits: 0,
      }).format(Math.abs(numeric))}`;
    },
    formatPercent(value) {
      const numeric = Number(value);
      if (!Number.isFinite(numeric)) return '—';
      return `${new Intl.NumberFormat('es-CL', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      }).format(numeric * 100)}%`;
    },
    formatRoas(value) {
      const numeric = Number(value);
      if (!Number.isFinite(numeric)) return '—';
      return `${new Intl.NumberFormat('es-CL', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(numeric)}×`;
    },
    formatDate(value) {
      if (!value) return '—';
      const parsed = new Date(`${String(value).slice(0, 10)}T12:00:00`);
      if (Number.isNaN(parsed.getTime())) return value;
      return new Intl.DateTimeFormat('es-CL', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      }).format(parsed);
    },
    shortDate(value, partial = false) {
      if (!value) return '';
      const parsed = new Date(`${String(value).slice(0, 10)}T12:00:00`);
      if (Number.isNaN(parsed.getTime())) return value;
      const label = new Intl.DateTimeFormat('es-CL', {
        day: '2-digit',
        month: 'short',
      }).format(parsed);
      return partial ? `${label}*` : label;
    },
    formatCutoff(value) {
      if (!value) return '—';
      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) return value;
      return new Intl.DateTimeFormat('es-CL', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'America/Santiago',
      }).format(parsed);
    },
    formatCacheAge(seconds) {
      const numeric = Number(seconds);
      if (!Number.isFinite(numeric) || numeric < 60) return 'menos de 1 min';
      return `${Math.max(1, Math.round(numeric / 60))} min`;
    },
    platformClass(platform) {
      return String(platform).toLowerCase().includes('meta') ? 'meta-dot' : 'google-dot';
    },
    numberValueClass(value) {
      const numeric = Number(value);
      if (!Number.isFinite(numeric) || numeric === 0) return '';
      return numeric > 0 ? 'positive-value' : 'negative-value';
    },
    profitValueClass(value) {
      const numeric = Number(value);
      if (!Number.isFinite(numeric)) return 'neutral-card';
      return numeric >= 0 ? 'profit-card' : 'loss-card';
    },
    roasValueClass(value) {
      const numeric = Number(value);
      if (!Number.isFinite(numeric)) return '';
      return numeric >= 1 ? 'positive-value' : 'negative-value';
    },
    signalLabel(signal) {
      return {
        mejora_observada: 'Mejora observada',
        deterioro_observado: 'Deterioro observado',
        estable: 'Estable',
        mixto: 'Mixto',
        datos_insuficientes: 'Datos insuficientes',
      }[signal] || 'Datos insuficientes';
    },
    signalClass(signal) {
      return `signal-${signal || 'datos_insuficientes'}`;
    },
  },
};
</script>

<style scoped>
.acquisition-profitability-report {
  color: #2b3f4d;
}

.profitability-heading,
.section-heading-row,
.report-meta-row,
.heading-actions {
  display: flex;
  align-items: center;
}

.profitability-heading {
  justify-content: space-between;
  gap: 24px;
  padding: 20px 22px;
  margin-bottom: 20px;
  border: 1px solid #dbe7ee;
  border-radius: 12px;
  background: linear-gradient(135deg, #f7fbfc 0%, #eef7f5 100%);
}

.section-eyebrow {
  display: block;
  margin-bottom: 5px;
  color: #188977;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.profitability-heading h3 {
  margin: 0 0 7px;
  color: #203745;
  font-size: 25px;
  font-weight: 750;
}

.profitability-heading p,
.section-heading-row p {
  margin: 0;
  color: #647684;
  font-size: 13px;
  line-height: 1.5;
}

.heading-actions {
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: wrap;
  min-width: 310px;
}

.report-pill,
.section-count,
.balance-badge {
  display: inline-flex;
  align-items: center;
  min-height: 32px;
  padding: 6px 11px;
  border: 1px solid #d7e2e8;
  border-radius: 999px;
  background: #fff;
  color: #50616d;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}

.partial-pill {
  border-color: #f0c980;
  background: #fff8e8;
  color: #8b611a;
}

.refresh-button {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-height: 36px;
  padding: 8px 13px;
  border: 0;
  border-radius: 7px;
  background: #246b7c;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}

.refresh-button:hover:not(:disabled) { background: #1d5968; }
.refresh-button:disabled { opacity: 0.65; cursor: wait; }

.profitability-alert {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 12px 14px;
  margin-bottom: 16px;
  border-radius: 8px;
  font-size: 13px;
}

.error-alert { border: 1px solid #e8b7b7; background: #fff5f5; color: #9d3434; }
.loading-alert { border: 1px solid #bdd9e2; background: #f2fafc; color: #356778; }

.profitability-loading {
  display: flex;
  min-height: 300px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 9px;
  color: #60727f;
}

.profitability-loading strong { color: #2b4555; font-size: 17px; }
.profitability-loading span { font-size: 13px; }

.loading-spinner {
  width: 34px;
  height: 34px;
  border: 3px solid #dce9ed;
  border-top-color: #188977;
  border-radius: 50%;
  animation: profitability-spin 0.8s linear infinite;
}

@keyframes profitability-spin { to { transform: rotate(360deg); } }

.report-meta-row {
  gap: 10px 22px;
  flex-wrap: wrap;
  padding: 10px 2px 16px;
  color: #6b7b86;
  font-size: 12px;
}

.report-meta-row strong { color: #344c5a; }

.financial-kpi-grid,
.volume-kpi-grid,
.coverage-grid {
  display: grid;
  gap: 14px;
}

.financial-kpi-grid {
  grid-template-columns: repeat(4, minmax(0, 1fr));
  margin-bottom: 14px;
}

.financial-kpi-card {
  position: relative;
  min-height: 128px;
  padding: 18px;
  overflow: hidden;
  border: 1px solid #dde6ec;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 4px 15px rgba(33, 55, 68, 0.05);
}

.financial-kpi-card::before {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 4px;
  background: #617a8a;
  content: '';
}

.income-card::before { background: #188977; }
.spend-card::before { background: #e28743; }
.profit-card::before { background: #2a9d6f; }
.loss-card::before { background: #c84f4f; }
.neutral-card::before { background: #7b8a95; }

.kpi-label {
  display: block;
  margin-bottom: 10px;
  color: #6a7a86;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
}

.financial-kpi-card strong {
  display: block;
  margin-bottom: 8px;
  color: #203746;
  font-size: 25px;
  line-height: 1.15;
}

.loss-card strong { color: #ad3f3f; }
.profit-card strong { color: #18795b; }
.financial-kpi-card small { color: #73828d; font-size: 11px; line-height: 1.35; }

.volume-kpi-grid {
  grid-template-columns: repeat(4, minmax(0, 1fr));
  margin-bottom: 26px;
}

.volume-kpi {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 15px;
  border: 1px solid #e1e8ed;
  border-radius: 8px;
  background: #f9fbfc;
}

.volume-kpi span { color: #687985; font-size: 12px; font-weight: 650; }
.volume-kpi strong { color: #263f4e; font-size: 19px; }

.report-section {
  margin-bottom: 24px;
  padding: 20px;
  border: 1px solid #e0e8ed;
  border-radius: 10px;
  background: #fff;
}

.section-heading-row {
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 16px;
  padding-bottom: 13px;
  border-bottom: 1px solid #edf1f4;
}

.section-heading-row h4 {
  margin: 0 0 4px;
  color: #2b4351;
  font-size: 18px;
  font-weight: 750;
}

.profitability-chart-container {
  min-height: 380px;
  padding: 12px 8px 0;
}

.profitability-table-wrap {
  overflow-x: auto;
  border: 1px solid #e3e9ed;
  border-radius: 8px;
}

.profitability-table {
  width: 100%;
  min-width: 930px;
  border-collapse: collapse;
  background: #fff;
}

.profitability-table th,
.profitability-table td {
  padding: 11px 12px;
  border-bottom: 1px solid #edf1f4;
  color: #3f515d;
  font-size: 12px;
  text-align: right;
  vertical-align: middle;
  white-space: nowrap;
}

.profitability-table th:first-child,
.profitability-table td:first-child { text-align: left; }
.profitability-table th { background: #f6f8fa; color: #63727d; font-size: 10px; letter-spacing: 0.03em; text-transform: uppercase; }
.profitability-table tr:last-child td { border-bottom: 0; }
.profitability-table tbody tr:hover { background: #fbfdfd; }

.platform-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  margin-right: 7px;
  border-radius: 50%;
}

.google-dot { background: #4285f4; }
.meta-dot { background: #7b56d8; }

.campaign-cell strong,
.campaign-cell small,
.platform-label { display: block; }
.campaign-cell strong { max-width: 330px; color: #2d4351; white-space: normal; line-height: 1.3; }
.campaign-cell small { margin-top: 3px; color: #7b8993; white-space: normal; }

.platform-label {
  width: fit-content;
  margin-bottom: 4px;
  padding: 2px 6px;
  border-radius: 999px;
  background: #edf4fb;
  color: #3d6d9a;
  font-size: 9px;
  font-weight: 800;
  text-transform: uppercase;
}

.neutral-label { background: #eef3f1; color: #587268; }
.positive-value { color: #16805f !important; font-weight: 750; }
.negative-value { color: #b34242 !important; font-weight: 750; }

.signal-badge {
  display: inline-flex;
  padding: 5px 8px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 750;
}

.signal-mejora_observada { background: #e5f6ef; color: #147454; }
.signal-deterioro_observado { background: #fdecec; color: #a63f3f; }
.signal-estable { background: #edf4fb; color: #3c6588; }
.signal-mixto { background: #fff4df; color: #95631f; }
.signal-datos_insuficientes { background: #f0f2f4; color: #687680; }

.scope-note {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  margin-top: 13px;
  padding: 11px 13px;
  border-radius: 7px;
  background: #fff8e9;
  color: #7c5b22;
  font-size: 12px;
  line-height: 1.45;
}

.two-column-sections {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px;
}

.compact-section { min-width: 0; }
.compact-table { min-width: 630px; }

.coverage-grid {
  grid-template-columns: repeat(4, minmax(0, 1fr));
  margin-bottom: 17px;
}

.coverage-grid > div {
  padding: 12px;
  border-radius: 7px;
  background: #f7fafb;
}

.coverage-grid span,
.coverage-grid strong { display: block; }
.coverage-grid span { margin-bottom: 5px; color: #6c7b86; font-size: 11px; }
.coverage-grid strong { color: #304754; font-size: 17px; }

.balance-badge { background: #fff5ea; color: #9b6022; }
.balance-badge.balanced { border-color: #bedfd3; background: #eef9f5; color: #21745b; }

.warning-list {
  margin: 0;
  padding-left: 19px;
  color: #657580;
  font-size: 12px;
  line-height: 1.55;
}

.warning-list li + li { margin-top: 6px; }
.empty-state { margin: 0; padding: 28px 12px; color: #7b8992; text-align: center; font-size: 13px; }

@media (max-width: 1180px) {
  .financial-kpi-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .two-column-sections { grid-template-columns: 1fr; }
}

@media (max-width: 760px) {
  .profitability-heading { align-items: flex-start; flex-direction: column; padding: 17px; }
  .heading-actions { justify-content: flex-start; min-width: 0; }
  .financial-kpi-grid,
  .volume-kpi-grid,
  .coverage-grid { grid-template-columns: 1fr; }
  .financial-kpi-card { min-height: 112px; }
  .section-heading-row { align-items: flex-start; flex-direction: column; }
  .report-section { padding: 15px; }
  .profitability-chart-container { min-height: 330px; padding: 0; }
}
</style>
